import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Ban,
  CheckCircle2,
  Check,
  Clock,
  Copy,
  Loader2,
  Trash2,
  XCircle,
} from "lucide-react";
import { ARTIFACTS_DELETED_NOTE, statusChipLabel } from "@/lib/jobStatus";
import { Badge } from "@/components/ui/badge";
import { CacheVerdictChip } from "@/components/CacheChip";
import { cn } from "@/lib/utils";
import {
  hasLiveRows,
  jobRowView,
  type JobRowInput,
  type JobRowView,
} from "@/lib/jobsTable";

// Compact jobs list. Design, column set and width budgets:
// internal/compiler/plans/2026-10-14-jobs-table-compact.md
// Cell logic lives in lib/jobsTable.ts (unit-tested); this file only renders.
//
// >= 720px: a fixed-layout table (6 columns, no horizontal scroll at
// 1280-1440px). < 720px: each job is a stacked card. Between, a very narrow
// container scrolls the table itself (min-w), never the page.

const STATUS_VARIANT: Record<
  JobRowInput["status"],
  "secondary" | "destructive" | "success" | "warning"
> = {
  queued: "secondary",
  running: "warning",
  succeeded: "success",
  failed: "destructive",
  cancelled: "secondary",
};

const STATUS_ICON = {
  queued: Clock,
  running: Loader2,
  succeeded: CheckCircle2,
  failed: XCircle,
  cancelled: Ban,
} as const;

export function StatusPill({
  status,
  deleted,
}: {
  status: JobRowInput["status"];
  /** Artifacts deleted with a published build (lib/jobStatus.ts). */
  deleted?: boolean;
}) {
  const Icon = deleted ? Trash2 : STATUS_ICON[status];
  return (
    <Badge
      variant={deleted ? "outline" : STATUS_VARIANT[status]}
      className="gap-1 whitespace-nowrap"
      title={
        deleted ? `${ARTIFACTS_DELETED_NOTE} (outcome: ${status})` : undefined
      }
    >
      <Icon
        className={cn(
          "h-3 w-3 shrink-0",
          status === "running" && !deleted && "animate-spin",
        )}
        aria-hidden="true"
      />
      {statusChipLabel(status, { artifacts_deleted_at: deleted ? 1 : null })}
    </Badge>
  );
}

/** The failure class chip of a failed job (its one-line cause is the hover
 *  text and shown in full on the job page / sibling rows). */
function FailureBadge({ view }: { view: JobRowView["failure"] }) {
  if (!view) return null;
  return (
    <Badge
      variant={view.tone}
      className="cursor-help whitespace-nowrap"
      title={view.cause ? `${view.cause}\n(${view.title})` : view.title}
      data-testid="failure-chip"
      data-failure-class={view.id}
    >
      {view.label}
    </Badge>
  );
}

/** The one-line cause under the entry path. */
function FailureCause({ view }: { view: JobRowView["failure"] }) {
  if (!view?.cause) return null;
  return (
    <div
      className="mt-1 break-words text-xs text-muted-foreground"
      data-testid="failure-cause"
      title={view.cause}
    >
      {view.cause}
    </div>
  );
}

function CopyId({ view }: { view: JobRowView }) {
  const [copied, setCopied] = useState(false);
  function copy(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard
      ?.writeText(view.id)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {});
  }
  const Icon = copied ? Check : Copy;
  return (
    <button
      type="button"
      onClick={copy}
      title={`${view.id} — click to copy`}
      aria-label={`Copy job id ${view.id}`}
      className="inline-flex items-center gap-1 rounded px-1 font-mono text-xs tabular-nums hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {view.shortId}
      <Icon className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
    </button>
  );
}

function EntryCell({ view }: { view: JobRowView }) {
  const { entry, build } = view;
  return (
    <div className="min-w-0 space-y-0.5">
      <Link
        to={`/logs/${encodeURIComponent(view.id)}`}
        title={entry.full}
        onClick={(e) => e.stopPropagation()}
        className="block truncate font-mono text-xs hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {entry.text}
      </Link>
      {(build.chip || build.platform) && (
        <div className="flex min-w-0 items-center gap-1.5 text-[11px] leading-4">
          {build.chip && (
            <Badge
              variant="info"
              title={build.chipTitle ?? undefined}
              className="min-w-0 cursor-help truncate px-1.5 py-0 text-[11px] font-medium"
            >
              <span className="truncate">{build.chip}</span>
            </Badge>
          )}
          {build.platform && (
            <span className="shrink-0 truncate font-mono text-muted-foreground">
              {build.platform}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function WhoCell({ view }: { view: JobRowView }) {
  const { who } = view;
  return (
    <div className="min-w-0 space-y-0.5" title={who.title}>
      <div
        className={cn(
          "truncate text-xs",
          who.isSystem && "text-muted-foreground",
        )}
      >
        {who.org}
      </div>
      <div className="truncate text-[11px] leading-4 text-muted-foreground">
        {who.user ?? "—"}
      </div>
    </div>
  );
}

function SizeCell({ view }: { view: JobRowView }) {
  const { size } = view;
  return (
    <div className="space-y-0.5" title={size.title}>
      <div className="whitespace-nowrap text-xs tabular-nums">{size.text}</div>
      {(view.cache || size.dynamic) && (
        <div className="flex flex-wrap items-center gap-1">
          {view.cache && <CacheVerdictChip verdict={view.cache} />}
          {size.dynamic && (
            <Badge
              variant="warning"
              className="px-1.5 py-0 text-[11px] font-medium"
            >
              dynamic
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

function MemCell({ view }: { view: JobRowView }) {
  return (
    <div
      className={cn(
        "whitespace-nowrap text-right text-xs tabular-nums",
        view.mem.text === "—" && "text-muted-foreground",
      )}
      title={view.mem.title}
      data-testid="peak-mem"
    >
      {view.mem.text}
    </div>
  );
}

function WhenCell({ view }: { view: JobRowView }) {
  const { when } = view;
  return (
    <div className="space-y-0.5" title={when.title}>
      <div className="whitespace-nowrap text-xs tabular-nums">
        {when.created}
      </div>
      {when.detail && (
        <div
          className={cn(
            "whitespace-nowrap text-[11px] leading-4 tabular-nums text-muted-foreground",
            when.live && "text-yellow-600 dark:text-yellow-400",
          )}
        >
          {when.detail}
        </div>
      )}
    </div>
  );
}

/** Column budget at a 1280px viewport (944px of table): sums to 100%.
 *  Status first, Job id last so a scrolled narrow container shows the
 *  primary columns (status, entry/build, when) first. */
const COLUMNS: {
  key: string;
  label: string;
  width: string;
  title?: string;
  right?: boolean;
}[] = [
  { key: "status", label: "Status", width: "10%" },
  { key: "entry", label: "Entry · Build", width: "30%" },
  { key: "when", label: "When", width: "15%" },
  { key: "who", label: "Org · User", width: "15%" },
  {
    key: "size",
    label: "Size · Cache",
    width: "11%",
    title:
      "Lines of code loaded / lines compiled, then the warm-cache verdict (warm, cold N = N cargo units that should have been reused, unknown) and a dynamic-code chip. Hover a cell for details.",
  },
  {
    key: "mem",
    label: "Peak mem",
    width: "9%",
    right: true,
    title:
      "Peak guest memory used during the build (MemTotal - MemAvailable). — = not recorded (older job). Hover a cell for exact bytes.",
  },
  { key: "job", label: "Job", width: "10%" },
];

export function JobsTable<T extends JobRowInput>({ jobs }: { jobs: T[] }) {
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const live = hasLiveRows(jobs);
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);

  const views = jobs.map((j) => jobRowView(j, now));
  const open = (id: string) => navigate(`/logs/${encodeURIComponent(id)}`);

  return (
    <>
      {/* >= 720px: fixed-layout table with a sticky header */}
      <div className="hidden max-h-[75vh] w-full overflow-auto min-[720px]:block">
        <table className="w-full min-w-[720px] table-fixed border-collapse text-sm">
          <colgroup>
            {COLUMNS.map((c) => (
              <col key={c.key} style={{ width: c.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  title={c.title}
                  className={cn(
                    "sticky top-0 z-10 border-b bg-card px-2 py-2 text-xs font-medium text-muted-foreground",
                    c.right ? "text-right" : "text-left",
                  )}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {views.map((v) => (
              <tr
                key={v.id}
                className="cursor-pointer border-b align-top transition-colors last:border-0 hover:bg-accent/50"
                onClick={() => open(v.id)}
              >
                <td className="px-2 py-2">
                  {/* Stack extra status chips here. */}
                  <div className="flex flex-col items-start gap-1">
                    <StatusPill
                      status={v.status}
                      deleted={v.artifactsDeleted}
                    />
                    <FailureBadge view={v.failure} />
                  </div>
                </td>
                <td className="px-2 py-2">
                  <EntryCell view={v} />
                  <FailureCause view={v.failure} />
                </td>
                <td className="px-2 py-2">
                  <WhenCell view={v} />
                </td>
                <td className="px-2 py-2">
                  <WhoCell view={v} />
                </td>
                <td className="px-2 py-2">
                  <SizeCell view={v} />
                </td>
                <td className="px-2 py-2">
                  <MemCell view={v} />
                </td>
                <td className="px-2 py-2">
                  <CopyId view={v} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* < 720px: one stacked card per job */}
      <ul className="space-y-2 min-[720px]:hidden">
        {views.map((v) => (
          <li
            key={v.id}
            className="cursor-pointer space-y-2 rounded-md border p-3 hover:bg-accent/50"
            onClick={() => open(v.id)}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1">
                <StatusPill status={v.status} deleted={v.artifactsDeleted} />
                <FailureBadge view={v.failure} />
              </div>
              <CopyId view={v} />
            </div>
            <EntryCell view={v} />
            <FailureCause view={v.failure} />
            <div className="grid grid-cols-2 gap-2">
              <WhenCell view={v} />
              <WhoCell view={v} />
            </div>
            <SizeCell view={v} />
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>Peak mem</span>
              <MemCell view={v} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
